import { ReactElement, useMemo, useState } from 'react'
import { filter } from 'lodash-es'
import { Box, Stack, Typography } from '@mui/material'
import { UseMutationResult } from '@tanstack/react-query'
import { SelectableButton } from '@ui/actions'
import { SavingIndicator } from '@ui/feedback'
import { AssemblyDataGrid } from 'src/components/manager/assemblies/AssemblyDataGrid'
import { assemblySchema } from 'src/components/manager/assemblies/AssemblyForm/schema'
import { assemblyKinds, assemblyTypes } from 'src/components/manager/assemblies/constants'
import {
  getAllowedUsageTypesForDuplication,
  prepareAssemblyForDuplication,
} from 'src/components/manager/assemblies/utils/duplicateAssembly'
import { getDefaultShortName, getShortNameForCopy } from 'src/utils/assemblies'

interface Props {
  assemblies: Assembly[]
  assignments: AssemblyAssignment[]
  assemblyType: AssemblyTypes
  onDelete: (assembly: Assembly) => Promise<Assembly[]>
  onSetStandard: (assembly: Assembly) => void
  isSettingStandard: boolean
  onDownload: (assembly: Assembly) => Promise<void>
  onStore?: (assembly: Assembly) => Promise<CLTAssembly | TimberFrameAssembly>
  setCreateValues: (assembly: Assembly, allowedUsageTypes?: AssemblyTypes[]) => void
  setEditValues: (assembly: Assembly) => void
  editMutation?: UseMutationResult<CLTAssembly | TimberFrameAssembly, unknown, Assembly, unknown>
  earthMassForce: number
}

const AssemblyTypeOverview = ({
  assemblies,
  assignments,
  assemblyType,
  onDelete,
  onSetStandard,
  isSettingStandard,
  onDownload,
  onStore,
  setCreateValues,
  setEditValues,
  editMutation,
  earthMassForce,
}: Props): ReactElement => {
  const assemblyUsageTypeLabel = useMemo(() => {
    return assemblyTypes.find(type => type.usage_type === assemblyType)?.label
  }, [assemblyType])
  const assembliesForType = useMemo(() => {
    return assemblies.filter(assembly => assembly.usage_type === assemblyType)
  }, [assemblies, assemblyType])

  const assemblyKindOptions = useMemo(() => {
    return filter(assemblyKinds, kind => kind.enabledOn.includes(assemblyType))
  }, [assemblyType])

  const [isEditingCell, setIsEditingCell] = useState(false)

  return (
    <>
      <Box>
        <Box mb={3} key={assemblyType}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">{assemblyUsageTypeLabel}</Typography>

            <Stack direction="row" alignItems="center" gap={2}>
              <SavingIndicator loading={isEditingCell} />
              <SelectableButton
                onClick={({ value: kind }: { value: AssemblyKind }) =>
                  setCreateValues({
                    ...assemblySchema.getDefault(),
                    usage_type: assemblyType,
                    kind,
                    isStandard: false,
                    short_name: getDefaultShortName(assemblyType, assemblies),
                  })
                }
                options={assemblyKindOptions.map(kind => ({
                  label: kind.label,
                  value: kind.type,
                }))}
                buttonProps={{
                  variant: 'outlined',
                  color: 'primary',
                  size: 'small',
                  sx: {
                    alignSelf: 'center',
                  },
                }}
                data-cy={`${assemblyType}-select`}
              >
                anlegen
              </SelectableButton>
            </Stack>
          </Stack>

          <AssemblyDataGrid
            assemblies={assembliesForType}
            catalogAssemblies={assemblies}
            usage_type={assemblyType as AssemblyTypes}
            assignments={assignments}
            onDelete={onDelete}
            onEdit={assembly => {
              setEditValues(assembly)
            }}
            onSetStandard={onSetStandard}
            isSettingStandard={isSettingStandard}
            onDownload={onDownload}
            onStore={onStore}
            onDuplicate={assembly => {
              // For duplicate button: restrict usage types in form, don't show error
              const allowedUsageTypes = getAllowedUsageTypesForDuplication(assembly.usage_type)
              // Use prepareAssemblyForDuplication to properly transform the assembly
              const duplicated = prepareAssemblyForDuplication(assembly, assemblyType)
              setCreateValues(
                { ...duplicated, short_name: getShortNameForCopy(duplicated, assemblies) },
                allowedUsageTypes,
              )
            }}
            onMutateCell={async assembly => {
              setIsEditingCell(true)
              await editMutation?.mutateAsync(assembly)
              setIsEditingCell(false)
            }}
            earthMassForce={earthMassForce}
            editable={!isEditingCell}
          />
        </Box>
      </Box>
    </>
  )
}

export default AssemblyTypeOverview
